/**
 * TestimonialsBlock - Customer testimonials grid
 *
 * This component renders customer testimonials in a responsive grid layout.
 * Design reference: TWBlocks Testimonial patterns
 *
 * Features:
 * - Responsive grid layout
 * - Author name, role, and quote
 * - Optional author images
 * - Clean card-based design
 */

export interface TestimonialItem {
	quote?: string
	author?: string
	role?: string
	image?: string
}

export interface TestimonialsBlockProps {
	title?: string
	subtitle?: string
	items?: TestimonialItem[]
}

export function TestimonialsBlock({
	title = 'What Our Customers Say',
	subtitle = 'Hear from people who love our product',
	items = [],
}: TestimonialsBlockProps) {
	// Default items if none provided
	const displayItems =
		items.length > 0
			? items
			: [
					{
						quote:
							'This product has completely transformed how we work. Highly recommended!',
						author: 'Sarah Johnson',
						role: 'CEO, TechCorp',
					},
					{
						quote:
							'Amazing customer support and a product that just works. Could not be happier.',
						author: 'Michael Chen',
						role: 'Product Manager, StartupCo',
					},
					{
						quote:
							'The best investment we made this year. ROI was evident within weeks.',
						author: 'Emily Davis',
						role: 'Director of Operations, Enterprise Inc',
					},
				]

	return (
		<section className="w-full py-16 md:py-24">
			<div className="container px-4 md:px-6">
				{/* Header */}
				<div className="mb-12 flex flex-col items-center text-center">
					{title && <h2 className="text-title-2 mb-4">{title}</h2>}
					{subtitle && (
						<p className="text-muted-foreground max-w-[700px] text-lg md:text-xl">
							{subtitle}
						</p>
					)}
				</div>

				{/* Testimonials Grid */}
				<div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
					{displayItems.map((item, index) => (
						<div
							key={index}
							className="bg-card flex flex-col gap-4 rounded-lg border p-6 shadow-sm"
						>
							{/* Quote */}
							{item.quote && (
								<blockquote className="text-foreground text-lg">
									"{item.quote}"
								</blockquote>
							)}

							{/* Author Info */}
							<div className="mt-auto flex items-center gap-3">
								{item.image && (
									<img
										src={item.image}
										alt={item.author || 'Testimonial author'}
										className="h-10 w-10 rounded-full object-cover"
									/>
								)}
								<div className="flex flex-col">
									{item.author && (
										<div className="font-semibold">{item.author}</div>
									)}
									{item.role && (
										<div className="text-muted-foreground text-sm">
											{item.role}
										</div>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	)
}
